import React, { lazy, Suspense, useEffect, useMemo, useState, useTransition } from "react"
import { Button, Container, Skeleton, useMediaQuery } from "@mui/material"

// Components
import Seo from "@/components/common/Seo"
import AboutProduct from "@/components/partials/productDetail/AboutProduct"
const RelatedProduct = lazy(() => import("@/components/partials/productDetail/RelatedProduct"))
import { Breadcrumb } from "@/components/common/Utils"
import { getProductDetailsData, resetProductDetails, setProductDetails } from "@/redux/reducers/categoryReducer"
import { ENDPOINTS } from "@/utils/constants"
import { useAppDispatch, useAppSelector } from "@/hooks"
import { setConfigDetails, setRecentlyViewedProduct, setStoreDetails } from "@/redux/reducers/homepageReducer"
import Loader from "@/components/common/Loader"
import useShowToaster from "@/hooks/useShowToaster"
const PageNotFound = lazy(() => import("@/components/partials/productDetail/PageNotFound"))
import classNames from "classnames"
import axiosInstance, { axiosWithContext } from "@/axiosfolder"
import useragent from 'express-useragent';
import { wrapPromise } from "@/utils/common"
import useSetConfigAndFavicon from "@/hooks/useSetConfigAndFavicon"
import useFirstImagePreloader from "@/hooks/useImagePreloader"
import BullionmarkHeader from "@/components/header/BullionmarkHeader"
import LazyHeader from "@/components/header/index"
import LazyBullionmarkFooter from "@/components/footer/BullionmarkFooter";
import LazyFooter from "@/components/footer/index";
import { useLocation } from "@reach/router"

interface ServerData {
  configDetails: any;
  storeDetails: any;
  footerData: any;
  configDetailsForRedux: any;
  topicPageData: {
    body: string;
    metaTitle: string;
    metaKeywords: string;
    metaDescription: string;
    systemName: string;
  };
  productDetailsData: any
  isMobile: boolean
  productFriendlyName: string
}
function ProductDetail({ serverData }: { serverData: ServerData }) {
  const dispatch = useAppDispatch()
  dispatch(setStoreDetails(serverData?.storeDetails))
  console.log("🚀 ~ ProductDetail ~ serverData:")
  const { showToaster } = useShowToaster();
  const checkLoadingStatus = useAppSelector(state => state.category.loading)
  const checkLoadingStatusOfTheGetWishlist = useAppSelector(state => state.wishList.loading)
  const location = useLocation();
  const [isRendering, setIsRendering] = useState(true);
  useFirstImagePreloader(serverData?.productDetailsData?.productImages);

  useEffect(() => {
    dispatch(setProductDetails(serverData?.productDetailsData))
  }, [serverData])
  useEffect(() => {
    if (serverData?.productDetailsData?.productId) {
      const recentlyViewedProducts = localStorage.getItem('recentlyViewedProducts')
      const parsedRecentlyViewedProducts: any[] = recentlyViewedProducts ? JSON.parse(recentlyViewedProducts) : null
      if (parsedRecentlyViewedProducts) {
        // if (!state.recentlyViewedProducts) return;
        const newProductId = serverData?.productDetailsData?.productId;
        // Check if the product already exists in the recently viewed list
        const existingIndex = parsedRecentlyViewedProducts.findIndex(productId => productId === newProductId);
        if (existingIndex === -1) {
          let updatedViewProducts = [newProductId, ...parsedRecentlyViewedProducts]
          if (updatedViewProducts?.length > 20) {
            updatedViewProducts.splice(0, 20)
          }
          localStorage.setItem('recentlyViewedProducts', JSON.stringify(updatedViewProducts))
        } else {
          let updatedViewProducts = [...parsedRecentlyViewedProducts]
          updatedViewProducts.splice(existingIndex, 1);
          updatedViewProducts.unshift(newProductId);
          localStorage.setItem('recentlyViewedProducts', JSON.stringify(updatedViewProducts))
        }
      } else {
        const firstViewedProduct = [serverData?.productDetailsData?.productId]
        localStorage.setItem('recentlyViewedProducts', JSON.stringify(firstViewedProduct))
      }
    }
  }, [serverData?.productDetailsData?.productId])
  useEffect(() => {
    if (serverData?.productDetailsData?.errorMessage) {
      showToaster({
        message: serverData?.productDetailsData?.errorMessage,
        severity: 'error'
      })
    }
  }, [serverData?.productDetailsData])

  const getFileExtension = (url) => {
    return url.split('.').pop().split('?')[0];
  };


  const keyWords = serverData?.productDetailsData?.metaKeywords?.split(',')?.length > 0 ? serverData?.productDetailsData?.metaKeywords?.split(',') : []
  const canonicalUrl = `${process.env.GATSBY_STORE_URL}${serverData?.productDetailsData?.friendlypagename}`;
  const imageUrl = `${serverData?.productDetailsData?.imageUrls ? serverData?.productDetailsData?.imageUrls[0]?.split("?")[0] : ""}`;
  const imageExtension = getFileExtension(imageUrl);
  useSetConfigAndFavicon(serverData)
  const [isPending, startTransition] = useTransition();
  useEffect(() => {
    startTransition(() => {
      // Simulating initial data fetch
      setTimeout(() => setIsRendering(false), 500);
    });
  }, [])

  const meta = [
    { name: "og:url", content: location.href },
    { name: "og:image", content: imageUrl },
    { name: "og:image:url", content: imageUrl },
    { name: "og:image:secure_url", content: imageUrl },
    { name: "og:image:type", content: `image/${imageExtension}` },
    { name: "og:image:width", content: "1200" },
    { name: "og:image:height", content: "630" },
    { name: "og:image:alt", content: serverData?.productDetailsData?.name },
    { name: "twitter:image", content: imageUrl },
    { name: "twitter:image:alt", content: serverData?.productDetailsData?.name },
    { name: "twitter:card", content: 'summary_large_image' },
    { name: "twitter:creator", content: 'summary_large_image' },
  ]
  // useRedirectTo404(serverData)
  return (
    <>
      <Seo
        lang="en"
        title={serverData?.productDetailsData?.metaTitle}
        keywords={(serverData?.productDetailsData?.metaKeywords ?? '')}
        description={serverData?.productDetailsData?.metaDescription}
        storeTitle={(serverData?.configDetails?.Store_Title?.value ?? '')}
        canonicalUrl={canonicalUrl}
        meta={meta}
      />

      {process.env.GATSBY_THEME_TYPE === "1" ? <BullionmarkHeader /> : <LazyHeader />}

      {checkLoadingStatus || checkLoadingStatusOfTheGetWishlist && <Loader open={checkLoadingStatus || checkLoadingStatusOfTheGetWishlist} />}

      {serverData?.productDetailsData && !serverData?.productDetailsData?.errorMessage ?
        (<>
          {/* <Breadcrumb arr={[{ navigate: (process.env.GATSBY_THEME_TYPE == '1' && process.env.GATSBY_STORE_CODE == '25' ? '/category/buy' : '/category/shop'), name: process.env.GATSBY_THEME_TYPE == '1' && process.env.GATSBY_STORE_CODE == '25' ? 'Buy' : 'Shop' },
          { navigate: '/' + serverData?.productFriendlyName, name: serverData?.productDetailsData?.name }]} /> */}
          <Breadcrumb
            needToAddHome={false}
            arr={[{
              navigate: '/',
              name: 'Home',
            }, ...Object.entries(serverData?.productDetailsData?.categoryProductBreadcrumb || {}).map(([key, value], index, array) => ({
              navigate: index === array.length - 1 ? value : `${value}`,
              name: key,
            }))]} />
          <Container id="PageProductDetail" className={classNames({ "BmkPageProductDetail": process.env.GATSBY_THEME_TYPE == '1' })}>

            {serverData?.productDetailsData?.productId && <AboutProduct productId={serverData?.productDetailsData?.productId} productDetailsData={serverData?.productDetailsData} configDetailsState={serverData?.configDetails} isMobile={serverData?.isMobile} />}

            {serverData?.productDetailsData?.relatedProducts?.length > 0 && <Suspense fallback={<></>}><RelatedProduct relatedProductsList={structuredClone(serverData?.productDetailsData?.relatedProducts)} heading={serverData?.configDetails["ProductDetails_RelatedProducts_SectionTitle"]?.value} description={serverData?.configDetails["ProductDetails_RelatedProducts_SectionSubtitle"]?.value} /></Suspense>}
          </Container></>) :
        <Suspense fallback={<></>}><PageNotFound /></Suspense>}
      {/* <RenderOnViewportEntry
        rootMargin="200px"
        threshold={0.25}
        minHeight={800}
      >
      </RenderOnViewportEntry> */}
      {process.env.GATSBY_THEME_TYPE === "1" ? <LazyBullionmarkFooter configDetails={serverData?.configDetails} footerDetails={serverData?.footerData} /> : <LazyFooter configDetails={serverData?.configDetails} footerDetails={serverData?.footerData} />}
    </>
  )
}

export default ProductDetail

export async function getServerData(context: {
  headers: any, params: any;
}) {
  try {
    const { params } = context;
    const axiosInstance = axiosWithContext(context);
    const userAgentHeader = context.headers.get('user-agent');
    const ua = userAgentHeader ? useragent.parse(userAgentHeader) : null;
    const isMobile = ua?.isMobile ? true : false;
    const productFriendlyName = params['product-friendlyName'];
    console.log("before fatching ", Date.now())

    const promises = [
      wrapPromise(axiosInstance.get(ENDPOINTS.getStoreConfigurationWithNavigationAndFooter)),
      wrapPromise(axiosInstance.get(ENDPOINTS.productDetails.replace('{{product-id}}', productFriendlyName))),
    ];

    // Wait for all promises to settle
    const results = await Promise.all(promises);

    // Extract the results
    const configDetailsResult = results[0];
    const productDetailsDataResult = results[1];

    // Handle results
    const storeDetailsData = configDetailsResult.status === 'fulfilled' ? configDetailsResult?.value?.data?.data : null;
    const configDetails = storeDetailsData?.configSettings;
    const productDetailsData = productDetailsDataResult.status === 'fulfilled' ? productDetailsDataResult?.value?.data?.data : null;
    const footerData = storeDetailsData?.footerLinks;
    // ================================================================================

    return {
      props: {
        configDetails: configDetails?.reduce((acc: any, curr: any) => {
          acc[curr.key] = curr
          return acc
        }, {}),
        configDetailsForRedux: configDetails,
        storeDetails: storeDetailsData,
        productDetailsData: productDetailsData,
        footerData: footerData,
        isMobile,
        productFriendlyName
      },
    };
  } catch (error) {
    console.error("🚀 ~ getServerData ~ error:", error);
    console.log("getServerData -- inside catch block", Date.now());
    return {
      status: 500,
      headers: {},
      props: {
        redirectTo404: true
      },
    };
  }
}
